import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBranding } from "../components/Layout";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";

export default function Ask() {
  const { user } = useAuth();
  const branding = useBranding();
  const firstName = branding.agent_first_name;
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/messages", { subject, body });
      setDone(true);
      setSubject(""); setBody("");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <span className="overline">Direct line</span>
      <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
        Ask {firstName} a question.
      </h1>
      <p style={{ color: "var(--text-muted)", maxWidth: 620, fontSize: "1.05rem" }}>
        Anything at all — a worry about a showing, a question about the contract, or a "is this normal?" moment. I'll get back to you quickly.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 28, marginTop: 40 }} className="ask-grid">
        <form onSubmit={submit} className="card" style={{ padding: 32 }} data-testid="ask-form">
          {done ? (
            <div style={{ textAlign: "center", padding: "32px 8px" }} data-testid="ask-success">
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#E7F0EA", color: "var(--success)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircle2 size={28} />
              </div>
              <h3 className="font-serif" style={{ fontSize: 26, margin: "16px 0 6px", fontWeight: 500 }}>Question sent!</h3>
              <p style={{ color: "var(--text-muted)" }}>{firstName} will reply to <b>{user?.email}</b> soon.</p>
              <button type="button" className="btn-secondary" onClick={() => setDone(false)} style={{ marginTop: 20 }} data-testid="ask-another-btn">
                Ask another question
              </button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Subject <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span></label>
                <input data-testid="ask-subject" className="input-field" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Buyer wants a second showing" />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Your question</label>
                <textarea data-testid="ask-body" className="input-field" rows={8} required value={body} onChange={(e) => setBody(e.target.value)} placeholder="What's on your mind?" />
              </div>
              {err && <div data-testid="ask-error" style={{ color: "var(--error)", fontSize: 14, marginTop: 12 }}>{err}</div>}
              <button data-testid="ask-submit-btn" type="submit" className="btn-primary" disabled={busy} style={{ marginTop: 20 }}>
                {busy ? "Sending…" : (<><Send size={14} /> Send to {firstName}</>)}
              </button>
            </>
          )}
        </form>

        <aside className="card" style={{ padding: 28, background: "var(--surface-alt)", border: "1px solid var(--border)" }} data-testid="ask-contact-card">
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--primary)" }}>
            <MessageCircle size={16} />
            <span className="overline" style={{ color: "var(--primary)" }}>Prefer a call?</span>
          </div>
          <h3 className="font-serif" style={{ fontSize: 26, fontWeight: 500, margin: "10px 0 6px", letterSpacing: "-0.01em" }}>{branding.agent_name}</h3>
          {branding.firm && <div style={{ color: "var(--text-muted)", fontSize: 14 }}>{branding.firm}</div>}
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 6, fontSize: 15 }}>
            {branding.agent_phone && <a href={`tel:${(branding.agent_phone || "").replace(/\D/g, "")}`} style={{ color: "var(--text)", fontWeight: 500 }}>{"\u{1F4DE} "}{branding.agent_phone}</a>}
            {branding.agent_email && <a href={`mailto:${branding.agent_email}`} style={{ color: "var(--text)" }}>{"✉️ "}{branding.agent_email}</a>}
          </div>
          <p style={{ marginTop: 18, color: "var(--text-muted)", fontSize: 13, lineHeight: 1.5 }}>
            Messages sent through this form also come straight to my email, so either way I'll see it.
          </p>
        </aside>
      </div>
    </div>
  );
}
